import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import apiService, {
  type User,
  type Tenant,
  type AuthenticationResult,
  type RegisterTenantRequest,
  type LoginRequest
} from '../services/api';
import { getRoleFromToken } from '../utils/jwt';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const tenant = ref<Tenant | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const expiresAt = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const searchToken = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const isAdmin = computed(() => user.value?.role === 'Admin');
  const needsSetup = computed(
    () => tenant.value != null && !tenant.value.isSetupCompleted
  );

  // Actions
  function clearError() {
    error.value = null;
  }

  function setAuthData(authResult: AuthenticationResult) {
    if (!authResult.user.role) {
      authResult.user.role = getRoleFromToken(authResult.accessToken);
    }
    user.value = authResult.user;
    tenant.value = authResult.tenant;
    accessToken.value = authResult.accessToken;
    refreshToken.value = authResult.refreshToken;
    expiresAt.value = authResult.expiresAt;
    searchToken.value = authResult.searchToken;
    
    // Store in localStorage
    localStorage.setItem('accessToken', authResult.accessToken);
    localStorage.setItem('refreshToken', authResult.refreshToken);
    localStorage.setItem('expiresAt', authResult.expiresAt);
    localStorage.setItem('user', JSON.stringify(authResult.user));
    localStorage.setItem('tenant', JSON.stringify(authResult.tenant));
    localStorage.setItem('searchToken', authResult.searchToken);
  }

  function setTenantData(t: Tenant) {
    tenant.value = t;
    localStorage.setItem('tenant', JSON.stringify(t));
  }

  function clearAuthData() {
    user.value = null;
    tenant.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    expiresAt.value = null;
    searchToken.value = null;
    
    // Clear localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('searchToken');
  }

  function loadFromStorage() {
    try {
      const storedToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedExpiresAt = localStorage.getItem('expiresAt');
      const storedUser = localStorage.getItem('user');
      const storedTenant = localStorage.getItem('tenant');
      const storedSearchToken = localStorage.getItem('searchToken');

      if (storedToken && storedUser && storedTenant) {
        accessToken.value = storedToken;
        refreshToken.value = storedRefreshToken;
        expiresAt.value = storedExpiresAt;
        user.value = JSON.parse(storedUser) as User;
        if (user.value && !user.value.role) {
          user.value.role = getRoleFromToken(storedToken);
        }
        tenant.value = JSON.parse(storedTenant);
        searchToken.value = storedSearchToken;
      }
    } catch (e) {
      console.error('Failed to load auth data from storage:', e);
      clearAuthData();
    }
  }

  async function register(request: RegisterTenantRequest): Promise<boolean> {
    try {
      isLoading.value = true;
      clearError();

      const response = await apiService.register(request);
      
      if (response.success && response.data) {
        return true;
      } else {
        error.value = response.error || 'Registration failed';
        return false;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unexpected error occurred';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function login(request: LoginRequest): Promise<boolean> {
    try {
      isLoading.value = true;
      clearError();

      const response = await apiService.login(request);
      
      if (response.success && response.data) {
        return true;
      } else {
        error.value = response.error || 'Login failed';
        return false;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unexpected error occurred';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function confirmEmail(token: string): Promise<boolean> {
    try {
      isLoading.value = true;
      clearError();

      const response = await apiService.confirmEmail(token);

      if (response.success && response.data) {
        setAuthData(response.data);
        return true;
      } else {
        error.value = response.error || 'Email confirmation failed';
        return false;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unexpected error occurred';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function verifyMagicLink(token: string): Promise<boolean> {
    try {
      isLoading.value = true;
      clearError();

      const response = await apiService.verifyMagicLink(token);
      
      if (response.success && response.data) {
        setAuthData(response.data);
        return true;
      } else {
        error.value = response.error || 'Invalid or expired magic link';
        return false;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unexpected error occurred';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function acceptInvitation(token: string, firstName: string, lastName: string): Promise<boolean> {
    try {
      isLoading.value = true;
      clearError();

      const response = await apiService.acceptInvitation(token, firstName, lastName);

      if (response.success) {
        return true;
      } else {
        error.value = response.error || 'Failed to accept invitation';
        return false;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unexpected error occurred';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function refreshTokens(): Promise<boolean> {
    if (!refreshToken.value) {
      return false;
    }

    try {
      const response = await apiService.refreshToken(refreshToken.value);
      
      if (response.success && response.data) {
        setAuthData(response.data);
        return true;
      } else {
        clearAuthData();
        return false;
      }
    } catch (e) {
      clearAuthData();
      return false;
    }
  }

  function markSetupComplete() {
    if (tenant.value) {
      tenant.value = { ...tenant.value, isSetupCompleted: true };
      localStorage.setItem('tenant', JSON.stringify(tenant.value));
    }
  }

  function logout() {
    clearAuthData();
  }

  // Initialize store from localStorage
  loadFromStorage();

  return {
    // State
    user: readonly(user),
    tenant: readonly(tenant),
    isLoading: readonly(isLoading),
    error: readonly(error),
    expiresAt: readonly(expiresAt),
    searchToken: readonly(searchToken),

    // Getters
    isAuthenticated,
    isAdmin,
    needsSetup,

    // Actions
    register,
    login,
    confirmEmail,
    verifyMagicLink,
    acceptInvitation,
    refreshTokens,
    logout,
    markSetupComplete,
    clearError,
    loadFromStorage,
    setTenantData
  };
});